import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import {
  Component,
  effect,
  inject,
  input,
  OnInit,
  PLATFORM_ID,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { CardType } from 'src/app/enums/card-type';
import { Project } from 'src/app/interfaces/project';
import { Update } from 'src/app/interfaces/update';
import { AnalyticsService } from 'src/app/services/analytics.service';
import { UpdateService } from 'src/app/services/update.service';
import { DeviceService } from 'src/app/services/device.service';
import { PollChoiceVoteData, UpdateData } from 'src/app/interfaces/device-data';
import { WidgetService } from 'src/app/services/widget.service';
import { Customer } from 'src/app/interfaces/customer';
import { AnalyticsType } from 'src/app/enums/analytics-type';
import { CardComponent } from '../cards/card/card.component';
import { InlineSVGModule } from 'ng-inline-svg-2';
import {
  IonInfiniteScroll,
  IonInfiniteScrollContent,
} from '@ionic/angular/standalone';
import { StateNoticeComponent } from '../state-notice/state-notice.component';
import { FilterButtonComponent } from '../filter-button/filter-button.component';
import { LoaderComponent } from '../loader/loader.component';
import { SearchBoxComponent } from '../search-box/search-box.component';
import { PwaService } from 'src/app/services/pwa.service';

@Component({
  standalone: true,
  selector: 'app-update-list',
  templateUrl: './update-list.component.html',
  imports: [
    InlineSVGModule,
    CardComponent,
    TranslateModule,
    IonInfiniteScroll,
    IonInfiniteScrollContent,
    StateNoticeComponent,
    FilterButtonComponent,
    LoaderComponent,
    SearchBoxComponent,
  ],
  styleUrl: './update-list.component.scss',
})
export class UpdateListComponent implements OnInit {
  private readonly analyticsService = inject(AnalyticsService);
  private readonly updateService = inject(UpdateService);
  private readonly deviceService = inject(DeviceService);
  private readonly widgetService = inject(WidgetService);
  private readonly pwaService = inject(PwaService);
  private readonly platformId = inject(PLATFORM_ID);

  updates = input<Update[]>();
  totalUpdates = input<number>();
  project = input<Project>();
  customer = input<Customer>();
  target = input<number>();
  updatesState: Update[];
  totalUpdatesState: number;
  updatesDeviceData: Record<string, UpdateData> = {};
  CardType = CardType;
  categories: Record<string, string> = {};
  activeCategory: string | null = null;
  searchKeyword: string = '';
  isSearchVisible: boolean = false;
  ready: boolean;
  interactionsReady: boolean = false;
  page: number;
  perPage = 7;
  isEmbedded: boolean;
  private seenUpdates: Array<Update> = [];

  constructor() {
    this.isEmbedded = this.widgetService.getIsEmbedded();
    if (this.pwaService.isDesktop()) this.perPage *= 3;

    effect(() => {
      const updates = this.updates();
      if (updates !== undefined) {
        this.interactionsReady = false;
        this.updatesState = updates;
        this.totalUpdatesState = this.totalUpdates();
        this.page = 1 + Math.ceil(updates.length / this.perPage);
        this.ready = true;
        if (isPlatformBrowser(this.platformId)) {
          void this.getUpdatesDeviceData().finally(() => {
            this.interactionsReady = true;
          });
        } else {
          this.interactionsReady = true;
        }
      }
    });
  }

  async ngOnInit() {
    if (isPlatformServer(this.platformId)) {
      return;
    }

    try {
      this.categories = await this.updateService.getUpdateCategories();
    } catch (error) {
      console.log(error);
    }
  }

  onItemVisible(item: Update) {
    const isNew = !this.seenUpdates.includes(item);
    if (isNew) {
      this.analyticsService.logEvent(
        AnalyticsType.UPDATE_VIEW,
        {
          update_id: item.id,
          developer_id: item.customer?.id,
          area_follower: item.areaFollower,
        },
        this.project() ?? item.project
      );
      this.seenUpdates = [...this.seenUpdates, ...[item]];
    }
  }

  async loadUpdates(event = null) {
    this.interactionsReady = false;
    try {
      const updateResponse = this.project()
        ? await this.updateService.getAllUpdates(
            this.project(),
            this.setUrlParams()
          )
        : await this.updateService.getAllUpdatesForCustomer(
            this.customer(),
            this.setUrlParams()
          );

      const updates = updateResponse.member;
      this.updatesState =
        this.page === 1 ? updates : this.updatesState.concat(updates);
      this.totalUpdatesState = updateResponse.totalItems;
      this.page++;
      void this.getUpdatesDeviceData().finally(() => {
        this.interactionsReady = true;
      });
    } catch (error) {
      console.log(error);
      this.interactionsReady = true;
    } finally {
      if (event) event.target.complete();
      this.ready = true;
    }
  }

  updateFilter(category: string | null) {
    this.ready = false;
    this.activeCategory = category;
    this.page = 1;
    this.loadUpdates();
  }

  onFilterSelection(value: string | null) {
    this.updateFilter(value);
  }

  onSearchValueChange(searchValue: string) {
    this.ready = false;
    this.searchKeyword = searchValue;
    this.page = 1;
    this.loadUpdates();
  }

  onSearchStarted() {
    this.isSearchVisible = true;
  }

  onSearchClosed() {
    const wasSearching = this.searchKeyword.trim().length > 0;
    if (!this.pwaService.isDesktop()) {
      this.isSearchVisible = false;
    }
    this.searchKeyword = '';

    if (wasSearching) {
      this.page = 1;
      this.ready = false;
      this.loadUpdates();
    }
  }

  setUrlParams() {
    let params = new HttpParams()
      .set('page', this.page.toString())
      .set('perPage', this.perPage.toString());
    if (this.activeCategory)
      params = params.set('category', this.activeCategory);
    if (this.searchKeyword && this.searchKeyword.trim())
      params = params.set('search', this.searchKeyword.trim());
    return params;
  }

  async getUpdatesDeviceData() {
    const updateIds = this.updatesState?.map(({ id }) => id).join(',');
    const params = new HttpParams().set('update_ids', updateIds);
    try {
      const response = await this.deviceService.getData(params);
      this.updatesDeviceData = response.updates ?? {};
      this.applyPollVotesFromDeviceData(this.updatesDeviceData);
    } catch {
      // ignore
    }
  }

  private applyPollVotesFromDeviceData(
    updatesData: Record<string, UpdateData>
  ) {
    if (!this.updatesState?.length) {
      return;
    }

    this.updatesState = this.updatesState.map((update) => {
      if (!update.id || !update.poll?.choices?.length) {
        return update;
      }

      const pollChoices = updatesData[String(update.id)]?.pollChoices;
      if (!pollChoices?.length) {
        return update;
      }

      const votedChoiceIds = this.getVotedChoiceIds(pollChoices);
      const nextChoices = update.poll.choices.map((choice) => ({
        ...choice,
        voted: votedChoiceIds.has(choice.id),
      }));

      return {
        ...update,
        poll: {
          ...update.poll,
          choices: nextChoices,
        },
      };
    });
  }

  private getVotedChoiceIds(pollChoices: PollChoiceVoteData[]): Set<number> {
    const votedChoiceIds = new Set<number>();

    pollChoices.forEach((choiceState) => {
      if (choiceState.voted && typeof choiceState.id === 'number') {
        votedChoiceIds.add(choiceState.id);
      }
    });

    return votedChoiceIds;
  }
}
